import React, { useState } from 'react';
import {
  X,
  MessageSquarePlus,
  Send,
  CheckCircle2,
  RotateCcw,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { Button } from '@roger/ui-components';
import {
  checkoutOrder,
  checkinOrder,
  revertCheckoutOrder,
  revertCheckinOrder,
} from '../../services/api';
import type { OrderManagerItemResponse } from '../../services/api/types.gen';
import { useToast } from '../../hooks/useToastContext';
import NotesDialog from './NotesDialog';

type OrderManagerTabId = 'to-be-sent' | 'pending-signature' | 'received';
/**
 * What a bulk action resolves to. The generated client is configured not to
 * throw, so an HTTP failure (a 403 without permission, a 409 on a stale order)
 * arrives as a RESOLVED value carrying `error` rather than as a rejection.
 * Counting only rejections reported those as successes.
 */
type BulkActionResult = { error?: unknown };
type SelectedOrder = Pick<
  OrderManagerItemResponse,
  'id' | 'clinic_physician_id' | 'signature_required'
>;

interface OrderManagerActionsProps {
  activeTab: OrderManagerTabId;
  selectedOrders: SelectedOrder[];
  onClearSelection: () => void;
  onActionComplete: () => void;
}

const OrderManagerActions: React.FC<OrderManagerActionsProps> = ({
  activeTab,
  selectedOrders,
  onClearSelection,
  onActionComplete,
}) => {
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { error: showError } = useToast();
  const selectedIds = selectedOrders.map(order => order.id);
  const count = selectedIds.length;
  const hasMissingPhysician = selectedOrders.some(
    order => order.signature_required && !order.clinic_physician_id
  );
  const hasNoSignatureCompletedOrder =
    activeTab === 'received' &&
    selectedOrders.some(order => !order.signature_required);
  const missingPhysicianMessage = 'Physician required before sending.';
  const signatureRequiredOnlyMessage =
    'Only signature-required orders can move back to pending signature.';

  if (count === 0) return null;

  const executeBulkAction = async (
    action: (params: {
      path: { order_id: string };
    }) => Promise<BulkActionResult>,
    getFailureMessage: (failedCount: number, total: number) => string
  ) => {
    setIsLoading(true);
    const results = await Promise.allSettled(
      selectedIds.map(id => action({ path: { order_id: id } }))
    );
    // A rejection covers a network or thrown failure; a resolved `error` covers
    // the HTTP failures the client hands back instead of throwing.
    const failedCount = results.filter(
      r => r.status === 'rejected' || r.value.error != null
    ).length;
    if (failedCount > 0) {
      showError(getFailureMessage(failedCount, count));
    }
    setIsLoading(false);
    onClearSelection();
    onActionComplete();
  };

  const handleSendToPhysician = () => {
    void executeBulkAction(
      checkoutOrder,
      (failed, total) => `Failed to send ${failed} of ${total} orders`
    );
  };

  return (
    <>
      <div className='fixed bottom-6 left-1/2 z-50 -translate-x-1/2 transform'>
        <div className='flex max-w-[calc(100vw-2rem)] flex-wrap items-center gap-2 rounded-lg border bg-background px-4 py-2 shadow-lg'>
          <span className='text-sm font-medium'>{count} selected</span>
          <Button
            variant='ghost'
            size='sm'
            onClick={onClearSelection}
            disabled={isLoading}
          >
            <X className='h-4 w-4' />
          </Button>

          <div className='mx-1 h-6 w-px bg-border' />

          <Button
            variant='outline'
            size='sm'
            onClick={() => setNotesDialogOpen(true)}
            disabled={isLoading}
          >
            <MessageSquarePlus className='mr-1.5 h-4 w-4' />
            Notes
          </Button>

          {activeTab === 'to-be-sent' && (
            <>
              {hasMissingPhysician && (
                <span className='flex items-center gap-1 text-xs font-medium text-destructive'>
                  <AlertCircle className='h-3.5 w-3.5' />
                  Physician required
                </span>
              )}
              <Button
                size='sm'
                onClick={handleSendToPhysician}
                disabled={isLoading || hasMissingPhysician}
                title={
                  hasMissingPhysician ? missingPhysicianMessage : undefined
                }
              >
                {isLoading ? (
                  <Loader2 className='mr-1.5 h-4 w-4 animate-spin' />
                ) : (
                  <Send className='mr-1.5 h-4 w-4' />
                )}
                Mark as Sent Externally
              </Button>
            </>
          )}

          {activeTab === 'pending-signature' && (
            <>
              <Button
                variant='outline'
                size='sm'
                onClick={() =>
                  executeBulkAction(
                    revertCheckoutOrder,
                    (failed, total) =>
                      `Failed to move ${failed} of ${total} orders to To Be Sent`
                  )
                }
                disabled={isLoading}
              >
                {isLoading ? (
                  <Loader2 className='mr-1.5 h-4 w-4 animate-spin' />
                ) : (
                  <RotateCcw className='mr-1.5 h-4 w-4' />
                )}
                Move to To Be Sent
              </Button>
              <Button
                size='sm'
                onClick={() =>
                  executeBulkAction(
                    checkinOrder,
                    (failed, total) =>
                      `Failed to mark ${failed} of ${total} orders as received`
                  )
                }
                disabled={isLoading}
              >
                {isLoading ? (
                  <Loader2 className='mr-1.5 h-4 w-4 animate-spin' />
                ) : (
                  <CheckCircle2 className='mr-1.5 h-4 w-4' />
                )}
                Mark Received
              </Button>
            </>
          )}

          {activeTab === 'received' && (
            <>
              {hasNoSignatureCompletedOrder && (
                <span className='flex items-center gap-1 text-xs font-medium text-destructive'>
                  <AlertCircle className='h-3.5 w-3.5' />
                  Signature required only
                </span>
              )}
              <Button
                variant='outline'
                size='sm'
                onClick={() =>
                  executeBulkAction(
                    revertCheckinOrder,
                    (failed, total) =>
                      `Failed to move ${failed} of ${total} orders to pending signature`
                  )
                }
                disabled={isLoading || hasNoSignatureCompletedOrder}
                title={
                  hasNoSignatureCompletedOrder
                    ? signatureRequiredOnlyMessage
                    : undefined
                }
              >
                {isLoading ? (
                  <Loader2 className='mr-1.5 h-4 w-4 animate-spin' />
                ) : (
                  <RotateCcw className='mr-1.5 h-4 w-4' />
                )}
                Move to Pending Signature
              </Button>
            </>
          )}
        </div>
      </div>

      <NotesDialog
        open={notesDialogOpen}
        onOpenChange={setNotesDialogOpen}
        taskIds={selectedIds}
        mode='bulk'
        onSuccess={() => {
          onClearSelection();
          onActionComplete();
        }}
      />
    </>
  );
};

export default OrderManagerActions;
